/**
 * CR-6446/CR-6448: Shared collapsible section component for filter panels.
 *
 * Used by Supplier View filter panels (SO Headers, Forecast, etc.) to provide
 * accordion-style sections with:
 * - Chevron toggle indicator
 * - Blue dot for sections with active filters
 * - Smooth expand/collapse animation
 *
 * This component is extracted for reuse across all reworked filter panels.
 */
import { useState, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface CollapsibleSectionProps {
  /** Section heading text (rendered uppercase) */
  title: string;
  /** Whether this section has active filters (shows blue dot indicator) */
  isActive: boolean;
  /** Whether the section starts expanded (default: true) */
  defaultOpen?: boolean;
  /** Section content */
  children: ReactNode;
}

/**
 * Collapsible accordion section for Supplier View filter panels.
 *
 * Renders a clickable header with chevron and optional active indicator,
 * with content that expands/collapses on click.
 */
export function CollapsibleSection({
  title,
  isActive,
  defaultOpen = true,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            !open && "-rotate-90"
          )}
        />
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          {title}
        </span>
        {isActive && (
          <span className="h-2 w-2 rounded-full bg-primary" title="Has active filters" />
        )}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}
